import { Product, Category, PageSEO, SitePagesContent } from '../types';
import { BlogArticle } from '../types/blog';
import {
  getCategoryArabicSlug,
  getProductArabicSlug,
  getProductLocalizedTitle,
  getProductLocalizedDescription
} from './localization';

export interface SitemapSyncStats {
  urlCount: number;
  productCount: number;
  categoryCount: number;
  pageCount: number;
  imageCount: number;
  blogCount?: number;
  arabicUrlCount?: number;
}

export interface SitemapSyncResult extends SitemapSyncStats {
  success: boolean;
  message: string;
  bytes?: number;
  timestamp: string;
  xml: string;
  xmlAr?: string;
}

/**
 * Escapes characters for XML compliance and prevents double-escaping
 */
export function escapeXml(unsafe: string = ''): string {
  return unsafe
    .replace(/&amp;/g, '&')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Generates dedicated Arabic Permalinks sitemap-ar.xml with Google Image extensions for Coveristic (/ar/...)
 */
export function generateArabicSitemapXml(
  products: Product[],
  categories: Category[],
  baseUrl: string = 'https://coveristic.net',
  blogArticles?: BlogArticle[]
): { xmlAr: string; statsAr: SitemapSyncStats } {
  const cleanBase = baseUrl.trim().replace(/\/+$/, '');
  const today = new Date().toISOString().split('T')[0];

  const addedUrls = new Set<string>();
  const urlBlocks: { group: 'static' | 'category' | 'product' | 'blog'; xml: string }[] = [];
  let imageCount = 0;
  let pageCount = 0;
  let categoryCount = 0;
  let productCount = 0;
  let blogCount = 0;

  // 1. Core Arabic Storefront Pages (/ar/...)
  const arabicStaticPages: { path: string; priority: string; changefreq: string }[] = [
    { path: '/ar/', priority: '1.0', changefreq: 'daily' },
    { path: '/ar/shop', priority: '0.9', changefreq: 'daily' },
    { path: '/ar/offers', priority: '0.9', changefreq: 'daily' },
    { path: '/ar/custom-print', priority: '0.9', changefreq: 'weekly' },
    { path: '/ar/pricing', priority: '0.85', changefreq: 'weekly' },
    { path: '/ar/blog', priority: '0.85', changefreq: 'daily' },
    { path: '/ar/about', priority: '0.75', changefreq: 'monthly' },
    { path: '/ar/contact', priority: '0.8', changefreq: 'monthly' },
    { path: '/ar/terms', priority: '0.6', changefreq: 'monthly' },
    { path: '/ar/privacy', priority: '0.6', changefreq: 'monthly' }
  ];

  arabicStaticPages.forEach(p => {
    const loc = `${cleanBase}${p.path}`;
    if (!addedUrls.has(loc)) {
      addedUrls.add(loc);
      pageCount++;
      urlBlocks.push({
        group: 'static',
        xml: `  <url>
    <loc>${escapeXml(loc)}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>${p.changefreq}</changefreq>
    <priority>${p.priority}</priority>
  </url>`
      });
    }
  });

  // 2. Arabic Category Permalinks (/ar/category/<arabic-slug>)
  const activeCategories = (categories || []).filter(c => c && c.active !== false);
  activeCategories.forEach(cat => {
    const arCatSlug = getCategoryArabicSlug(cat.id);
    const catUrl = `${cleanBase}/ar/category/${arCatSlug}`;
    if (!addedUrls.has(catUrl)) {
      addedUrls.add(catUrl);
      categoryCount++;

      const imgUrl = cat.heroImage
        ? cat.heroImage.startsWith('http')
          ? cat.heroImage
          : `${cleanBase}${cat.heroImage.startsWith('/') ? '' : '/'}${cat.heroImage}`
        : null;

      let imgBlock = '';
      if (imgUrl) {
        imageCount++;
        const arCatTitle = arCatSlug.replace(/-/g, ' ');
        imgBlock = `
    <image:image>
      <image:loc>${escapeXml(imgUrl)}</image:loc>
      <image:title>${escapeXml(`تشكيلة ${arCatTitle} — كوفريستيك لبنان`)}</image:title>
      <image:caption>${escapeXml(`تسوق ${arCatTitle} بالطباعة المخصصة في لبنان مع التوصيل السريع`)}</image:caption>
    </image:image>`;
      }

      urlBlocks.push({
        group: 'category',
        xml: `  <url>
    <loc>${escapeXml(catUrl)}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>daily</changefreq>
    <priority>0.9</priority>${imgBlock}
  </url>`
      });
    }
  });

  // 3. Arabic Product Permalinks (/ar/product/<arabic-slug>)
  const activeProducts = (products || []).filter(p => p && !p.inRecycleBin && !p.unlisted);
  activeProducts.forEach(prod => {
    const arSlug = getProductArabicSlug(prod);
    const arTitle = getProductLocalizedTitle(prod, 'ar');
    const arDesc = getProductLocalizedDescription(prod, 'ar');
    const prodUrl = `${cleanBase}/ar/product/${arSlug}`;

    if (!addedUrls.has(prodUrl)) {
      addedUrls.add(prodUrl);
      productCount++;

      const lastmod = prod.updatedAt
        ? prod.updatedAt.split('T')[0]
        : prod.createdAt
        ? prod.createdAt.split('T')[0]
        : today;

      const priority = prod.featured || prod.badge ? '0.85' : '0.8';

      const images: string[] = [];
      if (prod.image && typeof prod.image === 'string' && !prod.image.startsWith('data:image/svg+xml')) {
        const full = prod.image.startsWith('http')
          ? prod.image
          : `${cleanBase}${prod.image.startsWith('/') ? '' : '/'}${prod.image}`;
        images.push(full);
      }

      if (Array.isArray(prod.additionalImages)) {
        prod.additionalImages.forEach((extra: string) => {
          if (extra && typeof extra === 'string' && !extra.startsWith('data:image/svg+xml')) {
            const fullExtra = extra.startsWith('http')
              ? extra
              : `${cleanBase}${extra.startsWith('/') ? '' : '/'}${extra}`;
            if (!images.includes(fullExtra)) {
              images.push(fullExtra);
            }
          }
        });
      }

      let imgBlock = '';
      if (images.length > 0) {
        images.forEach(img => {
          imageCount++;
          imgBlock += `
    <image:image>
      <image:loc>${escapeXml(img)}</image:loc>
      <image:title>${escapeXml(arTitle)}</image:title>
      <image:caption>${escapeXml(arDesc.slice(0, 160))}</image:caption>
    </image:image>`;
        });
      }

      urlBlocks.push({
        group: 'product',
        xml: `  <url>
    <loc>${escapeXml(prodUrl)}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>${priority}</priority>${imgBlock}
  </url>`
      });
    }
  });

  // 4. Arabic Blog Articles (/ar/blog/<slug>)
  const publishedBlogs = (blogArticles || []).filter(b => b && b.status === 'published' && b.slug);
  publishedBlogs.forEach(art => {
    const blogUrl = `${cleanBase}/ar/blog/${art.slug}`;
    if (!addedUrls.has(blogUrl)) {
      addedUrls.add(blogUrl);
      blogCount++;

      const lastmod = art.updatedAt
        ? art.updatedAt.split('T')[0]
        : art.publishedAt
        ? art.publishedAt.split('T')[0]
        : today;

      urlBlocks.push({
        group: 'blog',
        xml: `  <url>
    <loc>${escapeXml(blogUrl)}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`
      });
    }
  });

  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"',
    '        xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">'
  ];

  let currentGroup = '';
  urlBlocks.forEach(item => {
    if (item.group !== currentGroup) {
      currentGroup = item.group;
      const label =
        currentGroup === 'static'
          ? 'ARABIC STOREFRONT PAGES (/ar/)'
          : currentGroup === 'category'
          ? 'ARABIC CATEGORY PERMALINKS (/ar/category/...)'
          : currentGroup === 'product'
          ? 'ARABIC PRODUCT PERMALINKS (/ar/product/...)'
          : 'ARABIC BLOG GUIDES (/ar/blog/...)';
      lines.push(`\n  <!-- ==================== ${label} ==================== -->`);
    }
    lines.push(item.xml);
  });

  lines.push('\n</urlset>\n');

  return {
    xmlAr: lines.join('\n'),
    statsAr: {
      urlCount: addedUrls.size,
      productCount,
      categoryCount,
      pageCount,
      imageCount,
      blogCount
    }
  };
}

/**
 * Generates standards-compliant sitemap.xml with Google Image extensions for Coveristic
 */
export function generateSitemapXml(
  products: Product[],
  categories: Category[],
  pagesSEO?: PageSEO[],
  pagesContent?: SitePagesContent,
  baseUrl: string = 'https://coveristic.net',
  blogArticles?: BlogArticle[]
): { xml: string; stats: SitemapSyncStats } {
  const cleanBase = baseUrl.trim().replace(/\/+$/, '');
  const today = new Date().toISOString().split('T')[0];

  const addedUrls = new Set<string>();
  const urlBlocks: { group: 'static' | 'category' | 'product' | 'blog'; xml: string }[] = [];
  let imageCount = 0;
  let pageCount = 0;
  let categoryCount = 0;
  let productCount = 0;
  let blogCount = 0;

  // 1. Core Static Store Pages (English & Arabic /ar/ roots)
  const coreStaticPages: { path: string; priority: string; changefreq: string; title: string }[] = [
    { path: '/', priority: '1.0', changefreq: 'daily', title: 'Homepage Storefront & Showcase' },
    { path: '/ar/', priority: '1.0', changefreq: 'daily', title: 'Arabic Storefront (/ar/)' },
    { path: '/shop', priority: '0.9', changefreq: 'daily', title: 'Shop All Products & Categories' },
    { path: '/ar/shop', priority: '0.9', changefreq: 'daily', title: 'Arabic Shop Catalog' },
    { path: '/offers', priority: '0.9', changefreq: 'daily', title: 'Seasonal Offers & Bundle Deals' },
    { path: '/ar/offers', priority: '0.9', changefreq: 'daily', title: 'Arabic Seasonal Offers' },
    { path: '/custom-print', priority: '0.9', changefreq: 'weekly', title: 'Custom Print On Demand Studio' },
    { path: '/ar/custom-print', priority: '0.9', changefreq: 'weekly', title: 'Arabic Custom Print Studio' },
    { path: '/pricing', priority: '0.85', changefreq: 'weekly', title: 'Custom Print Pricing Guide (USD)' },
    { path: '/ar/pricing', priority: '0.85', changefreq: 'weekly', title: 'Arabic Pricing Guide' },
    { path: '/blog', priority: '0.9', changefreq: 'daily', title: 'Print On Demand Lebanon Blog & City Guides' },
    { path: '/about', priority: '0.7', changefreq: 'monthly', title: 'About Coveristic & Lebanese Craftsmanship' },
    { path: '/ar/about', priority: '0.7', changefreq: 'monthly', title: 'Arabic About Us' },
    { path: '/contact', priority: '0.8', changefreq: 'monthly', title: 'Contact Us & Direct Studio Inquiry' },
    { path: '/ar/contact', priority: '0.8', changefreq: 'monthly', title: 'Arabic Contact Us' }
  ];

  coreStaticPages.forEach(p => {
    const loc = `${cleanBase}${p.path}`;
    if (!addedUrls.has(loc)) {
      addedUrls.add(loc);
      pageCount++;
      urlBlocks.push({
        group: 'static',
        xml: `  <url>
    <loc>${escapeXml(loc)}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>${p.changefreq}</changefreq>
    <priority>${p.priority}</priority>
  </url>`
      });
    }
  });

  // Dynamic / Custom Pages from pagesSEO
  if (pagesSEO && Array.isArray(pagesSEO)) {
    pagesSEO.forEach(seoPage => {
      if (!seoPage || !seoPage.path) return;
      const cleanPath = seoPage.path === '/' ? '/' : (seoPage.path.startsWith('/') ? seoPage.path : `/${seoPage.path}`);
      if (cleanPath === '/rss' || cleanPath === '/sitemap' || cleanPath === '/admin' || cleanPath === '/search-console') return;
      const loc = `${cleanBase}${cleanPath}`;
      if (!addedUrls.has(loc)) {
        addedUrls.add(loc);
        pageCount++;
        const lastmod = seoPage.lastUpdated ? seoPage.lastUpdated.split('T')[0] : today;
        urlBlocks.push({
          group: 'static',
          xml: `  <url>
    <loc>${escapeXml(loc)}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`
        });
      }
    });
  }

  // 2. Product Categories (English & Arabic Permalinks)
  const activeCategories = (categories || []).filter(c => c && c.active !== false);
  activeCategories.forEach(cat => {
    const slug = cat.slug || cat.id;
    const arCatSlug = getCategoryArabicSlug(cat.id);
    const catUrl = `${cleanBase}/category/${slug}`;
    const arCatUrl = `${cleanBase}/ar/category/${arCatSlug}`;

    const imgUrl = cat.heroImage
      ? cat.heroImage.startsWith('http')
        ? cat.heroImage
        : `${cleanBase}${cat.heroImage.startsWith('/') ? '' : '/'}${cat.heroImage}`
      : null;

    if (!addedUrls.has(catUrl)) {
      addedUrls.add(catUrl);
      categoryCount++;
      let imgBlock = '';
      if (imgUrl) {
        imageCount++;
        imgBlock = `
    <image:image>
      <image:loc>${escapeXml(imgUrl)}</image:loc>
      <image:title>${escapeXml(`Coveristic ${cat.name} Collection`)}</image:title>
      <image:caption>${escapeXml(cat.tagline || `Custom ${cat.name} print on demand in Lebanon`)}</image:caption>
    </image:image>`;
      }

      urlBlocks.push({
        group: 'category',
        xml: `  <url>
    <loc>${escapeXml(catUrl)}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>daily</changefreq>
    <priority>0.9</priority>${imgBlock}
  </url>`
      });
    }

    if (!addedUrls.has(arCatUrl)) {
      addedUrls.add(arCatUrl);
      categoryCount++;
      urlBlocks.push({
        group: 'category',
        xml: `  <url>
    <loc>${escapeXml(arCatUrl)}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>daily</changefreq>
    <priority>0.85</priority>
  </url>`
      });
    }
  });

  // 3. Active Products (English & Arabic Permalinks)
  const activeProducts = (products || []).filter(p => p && !p.inRecycleBin && !p.unlisted);
  activeProducts.forEach(prod => {
    const slug = prod.slug || prod.id.toLowerCase();
    const arSlug = getProductArabicSlug(prod);
    const arTitle = getProductLocalizedTitle(prod, 'ar');
    const arDesc = getProductLocalizedDescription(prod, 'ar');
    const prodUrl = `${cleanBase}/product/${slug}`;
    const arProdUrl = `${cleanBase}/ar/product/${arSlug}`;

    const lastmod = prod.updatedAt
      ? prod.updatedAt.split('T')[0]
      : prod.createdAt
      ? prod.createdAt.split('T')[0]
      : today;

    const priority = prod.featured || prod.badge ? '0.85' : '0.8';

    const images: string[] = [];
    if (prod.image && typeof prod.image === 'string' && !prod.image.startsWith('data:image/svg+xml')) {
      const full = prod.image.startsWith('http')
        ? prod.image
        : `${cleanBase}${prod.image.startsWith('/') ? '' : '/'}${prod.image}`;
      images.push(full);
    }

    if (Array.isArray(prod.additionalImages)) {
      prod.additionalImages.forEach((extra: string) => {
        if (extra && typeof extra === 'string' && !extra.startsWith('data:image/svg+xml')) {
          const fullExtra = extra.startsWith('http')
            ? extra
            : `${cleanBase}${extra.startsWith('/') ? '' : '/'}${extra}`;
          if (!images.includes(fullExtra)) {
            images.push(fullExtra);
          }
        }
      });
    }

    if (!addedUrls.has(prodUrl)) {
      addedUrls.add(prodUrl);
      productCount++;
      let imgBlock = '';
      if (images.length > 0) {
        images.forEach(img => {
          imageCount++;
          imgBlock += `
    <image:image>
      <image:loc>${escapeXml(img)}</image:loc>
      <image:title>${escapeXml(prod.title)}</image:title>
      <image:caption>${escapeXml(
        prod.description
          ? prod.description.slice(0, 160)
          : `Custom printed ${prod.title} by Coveristic Lebanon`
      )}</image:caption>
    </image:image>`;
        });
      }

      urlBlocks.push({
        group: 'product',
        xml: `  <url>
    <loc>${escapeXml(prodUrl)}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>${priority}</priority>${imgBlock}
  </url>`
      });
    }

    if (!addedUrls.has(arProdUrl)) {
      addedUrls.add(arProdUrl);
      productCount++;
      let arImgBlock = '';
      if (images.length > 0) {
        arImgBlock = `
    <image:image>
      <image:loc>${escapeXml(images[0])}</image:loc>
      <image:title>${escapeXml(arTitle)}</image:title>
      <image:caption>${escapeXml(arDesc.slice(0, 160))}</image:caption>
    </image:image>`;
      }

      urlBlocks.push({
        group: 'product',
        xml: `  <url>
    <loc>${escapeXml(arProdUrl)}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>${priority}</priority>${arImgBlock}
  </url>`
      });
    }
  });

  // 4. Published Blog Articles & City Guides
  const publishedBlogs = (blogArticles || []).filter(b => b && b.status === 'published' && b.slug);
  publishedBlogs.forEach(art => {
    const blogUrl = `${cleanBase}/blog/${art.slug}`;
    if (!addedUrls.has(blogUrl)) {
      addedUrls.add(blogUrl);
      blogCount++;

      const lastmod = art.updatedAt
        ? art.updatedAt.split('T')[0]
        : art.publishedAt
        ? art.publishedAt.split('T')[0]
        : today;

      let imgBlock = '';
      if (art.featuredImage && !art.featuredImage.startsWith('data:')) {
        imageCount++;
        const fullImg = art.featuredImage.startsWith('http')
          ? art.featuredImage
          : `${cleanBase}${art.featuredImage.startsWith('/') ? '' : '/'}${art.featuredImage}`;
        imgBlock = `
    <image:image>
      <image:loc>${escapeXml(fullImg)}</image:loc>
      <image:title>${escapeXml(art.title)}</image:title>
      <image:caption>${escapeXml(art.excerpt ? art.excerpt.slice(0, 160) : art.title)}</image:caption>
    </image:image>`;
      }

      urlBlocks.push({
        group: 'blog',
        xml: `  <url>
    <loc>${escapeXml(blogUrl)}</loc>
    <lastmod>${lastmod}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>${imgBlock}
  </url>`
      });
    }
  });

  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"',
    '        xmlns:image="http://www.google.com/schemas/sitemap-image/1.1">'
  ];

  let currentGroup = '';
  urlBlocks.forEach(item => {
    if (item.group !== currentGroup) {
      currentGroup = item.group;
      const label =
        currentGroup === 'static'
          ? 'STATIC STORE PAGES'
          : currentGroup === 'category'
          ? 'PRODUCT CATEGORIES'
          : currentGroup === 'product'
          ? 'INDIVIDUAL PRODUCTS'
          : 'BLOG ARTICLES & LEBANON CITY GUIDES';
      lines.push(`\n  <!-- ==================== ${label} ==================== -->`);
    }
    lines.push(item.xml);
  });

  lines.push('\n</urlset>\n');

  const xml = lines.join('\n');
  const stats: SitemapSyncStats = {
    urlCount: addedUrls.size,
    productCount,
    categoryCount,
    pageCount,
    imageCount,
    blogCount
  };

  return { xml, stats };
}

/**
 * Automatically regenerates and syncs both sitemap.xml and sitemap-ar.xml to the server and local storage
 */
export async function syncSitemapToServer(
  products: Product[],
  categories: Category[],
  pagesSEO?: PageSEO[],
  pagesContent?: SitePagesContent,
  baseUrl: string = 'https://coveristic.net',
  blogArticles?: BlogArticle[]
): Promise<SitemapSyncResult> {
  const { xml, stats } = generateSitemapXml(products, categories, pagesSEO, pagesContent, baseUrl, blogArticles);
  const { xmlAr, statsAr } = generateArabicSitemapXml(products, categories, baseUrl, blogArticles);
  const timestamp = new Date().toISOString();

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem('coveristic_last_sitemap_xml', xml);
      localStorage.setItem('coveristic_last_sitemap_ar_xml', xmlAr);
      localStorage.setItem('coveristic_sitemap_updated_at', timestamp);
      localStorage.setItem('coveristic_sitemap_stats', JSON.stringify({ ...stats, arabicUrlCount: statsAr.urlCount }));
    } catch {}
  }

  let serverWrittenBytes = 0;

  try {
    const res = await fetch('/api/sitemap.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ xml, xmlAr, timestamp })
    });

    if (res.ok) {
      const data = await res.json();
      serverWrittenBytes = data.bytes || new Blob([xml]).size;
    }
  } catch (e) {
    console.warn('Sitemap server sync caught error:', e);
  }

  return {
    success: true,
    message: 'sitemap.xml & sitemap-ar.xml updated',
    bytes: serverWrittenBytes || new Blob([xml]).size,
    timestamp,
    xml,
    xmlAr,
    ...stats,
    arabicUrlCount: statsAr.urlCount
  };
}
